import { ref, computed, type ComputedRef, type Ref } from 'vue'
import axios from 'axios'

// =============================================================================
// Types
// =============================================================================

interface UseSeasonReturn {
    season: ComputedRef<string>
    seasonDisplay: ComputedRef<string>
    seasonLoaded: ComputedRef<boolean>
    fetchSeason: () => Promise<string>
}

// =============================================================================
// Shared State (Singleton)
// =============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1'
const FALLBACK_SEASON = '2025-26'

const seasonValue: Ref<string> = ref(FALLBACK_SEASON)
const loaded = ref(false)
let fetchPromise: Promise<string> | null = null

// =============================================================================
// useSeason Composable
// =============================================================================

export function useSeason(): UseSeasonReturn {
    const season = computed(() => seasonValue.value)
    const seasonDisplay = computed(() => `Temporada ${seasonValue.value}`)
    const seasonLoaded = computed(() => loaded.value)

    const fetchSeason = async (): Promise<string> => {
        if (loaded.value) return seasonValue.value

        // Deduplicate concurrent calls
        if (fetchPromise) return fetchPromise

        fetchPromise = (async () => {
            try {
                const response = await axios.get<{ season: string }>(
                    `${API_BASE_URL}/config/season`,
                    { timeout: 5000 }
                )
                if (response.data?.season) {
                    seasonValue.value = response.data.season
                }
            } catch {
                // Silently fall back to default
            } finally {
                loaded.value = true
                fetchPromise = null
            }
            return seasonValue.value
        })()

        return fetchPromise
    }

    // Kick off fetch on first use
    if (!loaded.value && !fetchPromise) {
        fetchSeason()
    }

    return {
        season,
        seasonDisplay,
        seasonLoaded,
        fetchSeason
    }
}
